import { Box, Flex, Grid } from '@/components/atoms/Grid'
import { Body2 } from '@/components/atoms/typography'
import {
  Product,
  ProductRateData,
  ProductRateRow,
  Sdk
} from '@/network/graphql.g'
import { FC, memo, useEffect, useMemo, useState } from 'react'
import styled from 'styled-components'
import { useRouter } from 'next/router'
import { getPageFromRouterOrDefault } from '@/utils/urlHelper'
import Pager from '@/components/molecules/pager'
import useDevice from 'hooks/useDevice'
import { useIsHydrated } from 'hooks/useIsHydrated'
import { validFor } from '@/theme/validFor'
import ReviewTabletMobile from './reviewTabletMobile'
import ReviewDesktop from './reviewDesktop'
import useTranslation from 'next-translate/useTranslation'
import useGraphQl from 'hooks/useGraphQl'
import useSWR from 'swr'

type ReviewsProps = {
  product: Product
  productRateData: ProductRateData
  isExpanded: boolean
}

export type MoreInfoProps = {
  [reviewId: string]: boolean
}

export type TranslatedReviewsProps = {
  [reviewId: string]: string
}

export type originLanguageProps = {
  [reviewId: string]: boolean
}

const Reviews: FC<ReviewsProps> = memo(
  ({ product, productRateData, isExpanded }) => {
    const router = useRouter()
    const { requestNotifyError } = useGraphQl()
    const { isDesktop, isTablet, isMobile } = useDevice()
    // The server does not know the screen, the layout switches after hydration
    const isHydrated = useIsHydrated()
    const isDesktopView = isHydrated && isDesktop
    const isTabletOrMobileView = isHydrated && (isTablet || isMobile)
    const isMobileView = isHydrated && isMobile
    const { t } = useTranslation('common')
    const [isMoreOpen, setIsMoreOpen] = useState<MoreInfoProps>({})
    const [isMoreInfoOpen, setIsMoreInfoOpen] = useState<MoreInfoProps>({})
    const [translatedReviews, setTranslatedReviews] =
      useState<TranslatedReviewsProps>({})
    const reviewsPerPage = 5
    const numberOfPages = useMemo(
      () => Math.ceil(productRateData?.rateCounts / reviewsPerPage),
      [productRateData, reviewsPerPage]
    )
    const [isOriginLanguage, setIsOriginLanguage] =
      useState<originLanguageProps>({})
    const [page, setPage] = useState<number>(getPageFromRouterOrDefault(router))
    const [desktopReviews, setDesktopReviews] = useState<ProductRateRow[]>([])
    const [mobileReviews, setMobileReviews] = useState<ProductRateRow[]>([])
    const reviewsData = isDesktop ? desktopReviews : mobileReviews
    const showLoadMoreButton = page < numberOfPages

    const handleMoreClick = (reviewId: string) => {
      setIsMoreOpen((prevIsMoreOpen) => ({
        ...prevIsMoreOpen,
        [reviewId]: !prevIsMoreOpen[reviewId]
      }))
    }

    const handleMoreInfoClick = (reviewId: string) => {
      setIsMoreInfoOpen((prevIsMoreInfoOpen) => ({
        ...prevIsMoreInfoOpen,
        [reviewId]: !prevIsMoreInfoOpen[reviewId]
      }))
    }

    const handleOriginLanguageClick = (reviewId: string) => {
      setIsOriginLanguage((prevIsOriginLanguage) => ({
        ...prevIsOriginLanguage,
        [reviewId]: !prevIsOriginLanguage[reviewId]
      }))
    }

    useEffect(() => {
      setPage(getPageFromRouterOrDefault(router))
    }, [router.query.page])

    const fetcher = async () => {
      const { success, response } = await requestNotifyError((sdk: Sdk) => ({
        method: sdk.getProductRateRows,
        variables: {
          productId: product.id,
          page: page,
          limit: reviewsPerPage
        }
      }))

      if (success) {
        return response?.productRateRows
      } else {
        throw new Error('Failed to fetch data')
      }
    }

    const { data: reviews, mutate } = useSWR(
      isExpanded && page ? `api/product/${product.id}?page=${page}` : null,
      fetcher,
      {
        revalidateIfStale: false,
        revalidateOnFocus: false,
        revalidateOnReconnect: false
      }
    )

    const handlePageChange = () => {
      if (page !== getPageFromRouterOrDefault(router)) {
        mutate()
      }
    }

    const handleLoadMoreReviews = () => {
      const nextPage = page + 1
      setPage(nextPage)
      router.push(
        { query: { ...router.query, page: nextPage.toString() } },
        undefined,
        {
          shallow: true
        }
      )
    }

    useEffect(() => {
      if (reviews) {
        isDesktop
          ? setDesktopReviews(reviews)
          : setMobileReviews((prevReviews) => [...prevReviews, ...reviews])
      }
    }, [reviews])

    const fetchTranslateReview = async (reviewId: string) => {
      const { success, response } = await requestNotifyError((sdk: Sdk) => ({
        method: sdk.translateOrderProductRate,
        variables: {
          orderProductId: reviewId
        }
      }))
      if (success) {
        const translation = response.translateOrderProductRate.translation
        setTranslatedReviews((previousTranslatedReviews) => ({
          ...previousTranslatedReviews,
          [reviewId]: translation
        }))
      }
    }

    const reviewProps = {
      translatedReviews,
      fetchTranslateReview,
      isOriginLanguage,
      handleOriginLanguageClick
    }

    return (
      <Flex flexDirection="column" alignItems="center" px={{ desktop: '18px' }}>
        {isMobileView && (
          <StyledBody2>{t('ProductDetail.Reviews.reviews')}</StyledBody2>
        )}
        <Flex
          alignItems="center"
          flexDirection="column"
          width="100%"
          height="100%"
        >
          {reviewsData?.map((review) => (
            <ReviewContainer key={review?.id}>
              {isDesktopView && (
                <ReviewDesktop review={review} {...reviewProps} />
              )}
              {isTabletOrMobileView && (
                <ReviewTabletMobile
                  {...reviewProps}
                  review={review}
                  handleMoreClick={handleMoreClick}
                  handleMoreInfoClick={handleMoreInfoClick}
                  isMoreOpen={isMoreOpen}
                  isMoreInfoOpen={isMoreInfoOpen}
                />
              )}
            </ReviewContainer>
          ))}
        </Flex>

        <Box mt={3} mb={3}>
          {isDesktopView ? (
            <Pager
              onClick={handlePageChange}
              numberOfPages={numberOfPages}
              currentPageIndex={page}
            />
          ) : (
            showLoadMoreButton && (
              <LoadMoreButton onClick={handleLoadMoreReviews}>
                {t('ProductDetail.Reviews.loadMoreButton')}
              </LoadMoreButton>
            )
          )}
        </Box>
      </Flex>
    )
  }
)

export default Reviews

const StyledBody2 = styled(Body2)`
  align-self: flex-start;
  margin-bottom: 0;
`

const ReviewContainer = styled(Grid)`
  width: 100%;
  padding-block: 36px;
  gap: 16px;
  border-bottom: 1px solid ${(props) => props.theme.colors.listItemSeparator};
  ${validFor.desktop`
    grid-template-columns: 2fr 4fr 3.5fr;
    gap: 50px;
`};
`

const LoadMoreButton = styled.button`
  border: 1px solid ${(props) => props.theme.colors.onBackground};
  border-radius: ${(props) => props.theme.buttonBorderRadius}px;
  padding: 8px 12px;
  font-size: 12px;
  color: ${(props) => props.theme.colors.onBackground};
  background-color: ${(props) => props.theme.colors.background};
`
